import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Package, Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'

import type { ProductImportResult } from '@/lib/api/product-api'
import type { ProductsSearch } from '@/lib/products-list-search'

import { PRODUCTS_FILTERS, toIsActiveParam } from '@/lib/products-list-search'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { ListCard } from '@/components/common/ListCard'
import { ProductImportResultDialog } from '@/components/products/product-import-result-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useImportProducts, useProducts } from '@/hooks/use-product-query'
import {
    useInventoryByWarehouse,
    useWarehouses,
} from '@/hooks/use-inventory-query'
import { useAuth } from '@/hooks/use-auth'
import { formatQty, formatVND } from '@/lib/format'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

export type { ProductsSearch }

export const Route = createFileRoute('/_app/_authed/_sidebar/products/')({
    component: ProductsPage,
    validateSearch: PRODUCTS_FILTERS.validateSearch,
})

function ProductsPage() {
    const navigate = useNavigate({ from: '/products/' })
    const urlSearch = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.products, urlSearch)
    const { values, barProps, page, setPage } = useListFilters({
        def: PRODUCTS_FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    const [importResult, setImportResult] =
        useState<ProductImportResult | null>(null)
    const [importResultOpen, setImportResultOpen] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const importMutation = useImportProducts()
    const { hasPermission } = useAuth()
    // Warehouse staff get read-only access (products.read) to look up products
    // while receiving stock; create/import/edit stay with product managers.
    const canCreate = hasPermission('products.create')
    const canManage = hasPermission('products.update')

    // Listen for SSE product import result events dispatched by useSSENotifications
    useEffect(() => {
        const handler = (e: Event) => {
            const result = (e as CustomEvent<ProductImportResult>).detail
            setImportResult(result)
            setImportResultOpen(true)
        }
        window.addEventListener('product-import-result', handler)
        return () =>
            window.removeEventListener('product-import-result', handler)
    }, [])

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading } = useProducts({
        search: values.q || undefined,
        is_active: toIsActiveParam(values.active),
        category_code: values.category === 'all' ? undefined : values.category,
        page,
        per_page: 20,
    })

    // `sellable` lọc ở client vì API chưa có tham số tương ứng.
    const allProducts = data?.data ?? []
    const products =
        values.sellable === 'sellable'
            ? allProducts.filter((p) => p.is_sellable !== false)
            : values.sellable === 'internal'
              ? allProducts.filter((p) => p.is_sellable === false)
              : allProducts

    // Tồn theo warehouse mặc định để show 1 cột "Số lượng tồn" cạnh các cột giá.
    const { data: warehouses = [] } = useWarehouses()
    const defaultWarehouseId =
        warehouses.find((w) => w.is_default)?.id ?? warehouses[0]?.id
    const { data: inventoryData = [] } =
        useInventoryByWarehouse(defaultWarehouseId)
    const stockMap = inventoryData.reduce<Record<string, number>>(
        (acc, row) => {
            acc[row.product_id] = (acc[row.product_id] ?? 0) + row.quantity
            return acc
        },
        {},
    )
    const meta = data?.meta
    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!values.q
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.product_not_found()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    importMutation.mutate(file, {
                        onSuccess: () => {
                            toast.info(m.import_products_processing())
                        },
                        onError: () => toast.error(m.import_upload_failed()),
                    })
                    e.target.value = ''
                }}
            />
            <ListPageHeader
                icon={<Package className="size-5 shrink-0" />}
                title={m.product_title()}
                primaryActions={
                    canCreate ? (
                        <>
                            <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importMutation.isPending}
                            >
                                <Upload className="size-4" />
                                {importMutation.isPending
                                    ? m.import_importing()
                                    : m.import_excel()}
                            </Button>
                            <Button
                                size="sm"
                                className="w-full"
                                onClick={() =>
                                    navigate({ to: '/products/new' })
                                }
                            >
                                <Plus className="size-4" />
                                {m.product_add_new()}
                            </Button>
                        </>
                    ) : null
                }
            />

            <ListFilterBar {...barProps} />

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.common_loading()}
                    </div>
                ) : products.length === 0 ? (
                    emptyState
                ) : (
                    products.map((p) => {
                        const primaryImg =
                            p.images?.find((i) => i.is_primary) ?? p.images?.[0]
                        return (
                            <ListCard
                                key={p.id}
                                onClick={
                                    canManage
                                        ? () =>
                                              void navigate({
                                                  to: '/products/$productId',
                                                  params: { productId: p.id },
                                              })
                                        : undefined
                                }
                                title={
                                    <span className="flex items-center gap-2">
                                        {primaryImg ? (
                                            <img
                                                src={getProductImageURL(
                                                    primaryImg.image_url,
                                                )}
                                                alt=""
                                                className="size-10 shrink-0 rounded object-cover bg-muted"
                                            />
                                        ) : (
                                            <span className="size-10 shrink-0 rounded bg-muted flex items-center justify-center">
                                                <Package className="size-5 text-muted-foreground" />
                                            </span>
                                        )}
                                        <span className="font-medium break-words">
                                            {p.name}
                                        </span>
                                    </span>
                                }
                                subtitle={p.sku}
                                badge={
                                    <span className="flex flex-col items-end gap-1">
                                        <Badge
                                            variant={
                                                p.is_active
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                        >
                                            {p.is_active
                                                ? m.customer_active()
                                                : m.customer_inactive()}
                                        </Badge>
                                        {p.is_sellable === false ? (
                                            <Badge
                                                variant="secondary"
                                                className="bg-orange-100 text-orange-700"
                                            >
                                                {m.product_internal_badge()}
                                            </Badge>
                                        ) : null}
                                    </span>
                                }
                                metric={{
                                    label: m.product_unit_price(),
                                    value: `${formatVND(p.unit_price)} ₫`,
                                }}
                                fields={[
                                    {
                                        label: m.product_cost_price(),
                                        value: `${formatVND(p.cost_price)} ₫`,
                                    },
                                    {
                                        label: m.product_stock_qty_header(),
                                        value:
                                            typeof stockMap[p.id] === 'number'
                                                ? `${formatQty(stockMap[p.id])} ${p.unit}`
                                                : '—',
                                    },
                                    {
                                        label: m.product_category_field_label(),
                                        value: p.category_name || '—',
                                    },
                                    {
                                        label: m.product_barcode(),
                                        value: p.barcode ?? '—',
                                    },
                                ]}
                            />
                        )
                    })
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-14">
                                {m.product_image_header()}
                            </TableHead>
                            <TableHead>{m.product_sku()}</TableHead>
                            <TableHead>{m.product_barcode()}</TableHead>
                            <TableHead>{m.product_name()}</TableHead>
                            <TableHead>
                                {m.product_category_field_label()}
                            </TableHead>
                            <TableHead>{m.product_unit()}</TableHead>
                            <TableHead>{m.product_cost_price()}</TableHead>
                            <TableHead>{m.product_unit_price()}</TableHead>
                            <TableHead>
                                {m.product_stock_qty_header()}
                            </TableHead>
                            <TableHead>{m.product_status()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : products.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            products.map((p) => {
                                const primaryImg =
                                    p.images?.find((i) => i.is_primary) ??
                                    p.images?.[0]
                                return (
                                    <TableRow
                                        key={p.id}
                                        className={
                                            canManage
                                                ? 'cursor-pointer hover:bg-muted/50'
                                                : undefined
                                        }
                                        onClick={
                                            canManage
                                                ? () =>
                                                      navigate({
                                                          to: '/products/$productId',
                                                          params: {
                                                              productId: p.id,
                                                          },
                                                      })
                                                : undefined
                                        }
                                    >
                                        <TableCell>
                                            {primaryImg ? (
                                                <img
                                                    src={getProductImageURL(
                                                        primaryImg.image_url,
                                                    )}
                                                    alt={p.name}
                                                    className="size-10 rounded object-cover bg-muted"
                                                />
                                            ) : (
                                                <div className="size-10 rounded bg-muted flex items-center justify-center">
                                                    <Package className="size-5 text-muted-foreground" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {p.sku}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {p.barcode ?? '—'}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {p.name}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {p.category_name || '—'}
                                        </TableCell>
                                        <TableCell>{p.unit}</TableCell>
                                        <TableCell>
                                            {formatVND(p.cost_price)} ₫
                                        </TableCell>
                                        <TableCell>
                                            {formatVND(p.unit_price)} ₫
                                        </TableCell>
                                        <TableCell>
                                            {typeof stockMap[p.id] === 'number'
                                                ? `${formatQty(stockMap[p.id])} ${p.unit}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 items-start">
                                                <Badge
                                                    variant={
                                                        p.is_active
                                                            ? 'success'
                                                            : 'secondary'
                                                    }
                                                >
                                                    {p.is_active
                                                        ? m.customer_active()
                                                        : m.customer_inactive()}
                                                </Badge>
                                                {p.is_sellable === false ? (
                                                    <Badge
                                                        variant="secondary"
                                                        className="bg-orange-100 text-orange-700"
                                                    >
                                                        {m.product_internal_badge()}
                                                    </Badge>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.product_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}

            <ProductImportResultDialog
                result={importResult}
                open={importResultOpen}
                onOpenChange={setImportResultOpen}
            />
        </div>
    )
}
